// ============================================================================
// NextSessionCard — the next PT session + a "homework to do" chip (Wave 4).
// ----------------------------------------------------------------------------
// Surfaces home.next_session (the nearest upcoming planned PT session) as a
// glanceable card with the date/time and an optional type badge, plus a
// pending-homework chip driven by home.pending_homework_count that scrolls down
// to the coach-homework panel on the same dashboard. When there's no upcoming
// session a gentle empty state shows; the homework chip only appears when there
// is outstanding homework. Mobile-first, Pulse, reduced-motion friendly.
//
// All copy via i18n keys (clientPortal.next.*); the only server value rendered
// is the session's free-text type (trainer-authored, safe per backend contract).
// ============================================================================
import { useTranslation } from "react-i18next";
import { Badge, Card, CardHeader } from "../../components";
import type { Session } from "../../api";
import { fmtDateTime } from "./portalLib";
import { HOMEWORK_ANCHOR_ID } from "./dashboardAnchors";

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function HomeworkChip({ count }: { count: number }) {
  const { t } = useTranslation("clientPortal");
  const onClick = () => {
    const el = document.getElementById(HOMEWORK_ANCHOR_ID);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex items-center gap-2 rounded-pill bg-violet-soft px-3.5 py-2 text-sm font-bold text-ember-600 ring-1 ring-inset ring-violet/20 transition-colors hover:bg-violet/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
    >
      <span className="grid h-5 min-w-5 place-items-center rounded-full bg-grad-violet px-1 text-xs font-extrabold text-white">
        {count}
      </span>
      {t("next.homework", { count })}
      <span aria-hidden className="transition-transform group-hover:translate-y-0.5">↓</span>
    </button>
  );
}

export function NextSessionCard({
  session,
  pendingHomework,
}: {
  session: Session | null;
  pendingHomework: number;
}) {
  const { t } = useTranslation("clientPortal");
  const when = session?.scheduled_at ?? session?.performed_at ?? null;

  return (
    <Card className="space-y-4">
      <CardHeader title={t("next.title")} subtitle={t("next.subtitle")} />

      {session && when ? (
        <div className="flex items-center gap-3.5 rounded-lg bg-coral-soft px-4 py-4 ring-1 ring-inset ring-coral/15">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-grad-coral text-white shadow-volt">
            <CalendarIcon />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-extrabold tracking-tight text-body">
              {fmtDateTime(when)}
            </p>
            <p className="text-xs font-bold uppercase tracking-kicker text-volt-600">
              {t("next.ptSession")}
            </p>
          </div>
          {session.type && (
            <Badge tone="volt" className="shrink-0">
              {session.type}
            </Badge>
          )}
        </div>
      ) : (
        <div className="rounded-lg bg-surface-sunken px-4 py-6 text-center">
          <p className="font-display font-bold text-body">{t("next.empty")}</p>
          <p className="mt-1 text-sm text-body-soft">{t("next.emptyHint")}</p>
        </div>
      )}

      {pendingHomework > 0 && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-body-soft">{t("next.homeworkHint")}</p>
          <HomeworkChip count={pendingHomework} />
        </div>
      )}
    </Card>
  );
}
